import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { bannerComics, getComicById } from '@/mocks/comics';

const heroComics = bannerComics.map(id => getComicById(id)).filter(Boolean);

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroComics.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroComics.length) % heroComics.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const comic = heroComics[current];
  if (!comic) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/30 to-foreground-950/70"></div>
      </div>

      <div className="relative z-10 w-full h-full flex items-center">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-white whitespace-nowrap">
                {comic.status}
              </span>
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/20 text-white whitespace-nowrap">
                {comic.category}
              </span>
              <span className="text-white/70 text-xs">
                <i className="ri-star-fill text-accent-400 mr-0.5"></i>{comic.rating}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-heading text-white mb-3">{comic.title}</h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 line-clamp-3 max-w-lg">
              {comic.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/comic/${comic.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </Link>
              <Link
                to={`/category/${comic.categorySlug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/20 hover:bg-white/30 text-white font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                更多{comic.category}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="flex items-center gap-4 mt-8">
              <div className="text-white/80 text-xs">
                <i className="ri-eye-line mr-1"></i>
                {(comic.views / 10000).toFixed(0)}万阅读
              </div>
              <div className="text-white/80 text-xs">
                <i className="ri-file-list-3-line mr-1"></i>
                {comic.chapters.length}话
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-all cursor-pointer z-20"
        aria-label="Previous"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-all cursor-pointer z-20"
        aria-label="Next"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {heroComics.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              i === current ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}